"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Dropdown = Dropdown;
var _lib = require("@a1s/lib");
var _react = _interopRequireWildcard(require("react"));
var _styled = require("./styled");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
//
// Main component
// -------------------------------------------------------------------------------------------------

/**
 * The `Dropdown` component implements the [_listbox_ aria control][listbox], which presents a list of
 * selectable options in a popover, which is toggled on or off by a button. Listboxes work in much the
 * same way as a HTML `<select>` element.
 *
 * [listbox]: https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 *
 * @alpha
 */
function Dropdown({
  children,
  direction = 'down',
  disabled,
  onChange,
  testId,
  value
}) {
  const buttonIdRef = (0, _react.useRef)((0, _lib.random)(6));
  const listIdRef = (0, _react.useRef)((0, _lib.random)(6));
  const buttonElRef = (0, _react.useRef)();
  const listElRef = (0, _react.useRef)();
  const [isExpanded, setIsExpanded] = (0, _react.useState)(false);
  function createOptionClickHandler(value, callback) {
    return event => {
      if (callback) callback(event);
      if (onChange) onChange(value);
      window.setTimeout(() => {
        var _buttonElRef$current;
        return (_buttonElRef$current = buttonElRef.current) === null || _buttonElRef$current === void 0 ? void 0 : _buttonElRef$current.focus();
      }, 0);
    };
  }
  function handleBlur(event) {
    event.preventDefault();
    setIsExpanded(false);
  }
  function handleButtonClick(event) {
    event.preventDefault();
    setIsExpanded(true);
    // Use an animation frame to defer trying to focus the list element to
    // in order give React's render cycle a change to run first
    window.requestAnimationFrame(() => {
      var _listElRef$current;
      return void ((_listElRef$current = listElRef.current) === null || _listElRef$current === void 0 ? void 0 : _listElRef$current.focus());
    });
  }
  function handleListKeyDown(event) {
    var _buttonElRef$current2;
    const selected = event.currentTarget.querySelector('[aria-selected]');
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (onChange && selected !== null && selected !== void 0 && selected.nextSibling) {
          const value = selected.nextSibling.dataset['value'];
          if (value) onChange(value);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (onChange && selected !== null && selected !== void 0 && selected.previousSibling) {
          const value = selected.previousSibling.dataset['value'];
          if (value) onChange(value);
        }
        break;
      case 'Enter':
      case 'Escape':
        event.preventDefault();
        (_buttonElRef$current2 = buttonElRef.current) === null || _buttonElRef$current2 === void 0 ? void 0 : _buttonElRef$current2.focus();
        break;
      case 'Home':
        event.preventDefault();
        if (onChange) {
          const firstOption = event.currentTarget.querySelector('[data-value]:first-of-type');
          const value = firstOption === null || firstOption === void 0 ? void 0 : firstOption.dataset['value'];
          if (value) onChange(value);
        }
        break;
      case 'End':
        event.preventDefault();
        if (onChange) {
          const lastOption = event.currentTarget.querySelector('[data-value]:last-of-type');
          const value = lastOption === null || lastOption === void 0 ? void 0 : lastOption.dataset['value'];
          if (value) onChange(value);
        }
        break;
      default:
        console.log(event.key);
      // eslint-disable-line
    }
  }

  // This is displayed on the expander button to display the selected value. This
  // variable is set while the children are iterate over just below.
  let selectedLabel = null;

  // Figure out which of the options are selected and attach the interaction handler
  // to the HTML elements themselves
  const options = _react.default.Children.map(children, (child, i) => {
    if (! /*#__PURE__*/(0, _react.isValidElement)(child)) return null;

    // Decide if we're dealing with the selection option
    let isSelected = false;
    if (!value && i === 0) isSelected = true;
    if (value === child.props.value) isSelected = true;

    // Figure out what we need to display on the button as the selection value
    if (isSelected) selectedLabel = extractLabel(child);

    // Attach additional props and callbacks to the options that were passed in
    return /*#__PURE__*/(0, _react.cloneElement)(child, {
      'aria-selected': isSelected || undefined,
      onClick: createOptionClickHandler(child.props.value, child.props.onClick)
    });
  });
  return /*#__PURE__*/_react.default.createElement(_styled.StyledBase, null, /*#__PURE__*/_react.default.createElement(_styled.StyledButton, {
    "aria-expanded": isExpanded,
    "aria-haspopup": "listbox",
    "aria-labelledby": `${listIdRef.current} ${buttonIdRef.current}`,
    "data-testid": testId,
    disabled: disabled,
    id: buttonIdRef.current,
    onClick: handleButtonClick,
    ref: buttonElRef,
    type: "button"
  }, selectedLabel), /*#__PURE__*/_react.default.createElement(_styled.StyledList, {
    "aria-labelledby": buttonIdRef.current,
    direction: direction,
    hidden: !isExpanded,
    onBlur: handleBlur,
    onKeyDown: handleListKeyDown,
    ref: listElRef,
    role: "listbox",
    tabIndex: -1
  }, options));
}

//
// Sub-components
// -------------------------------------------------------------------------------------------------

Dropdown.Option = Option;
function Option({
  children,
  testId,
  value,
  ...props
}) {
  return /*#__PURE__*/_react.default.createElement(_styled.StyledItem, _extends({
    "data-testid": testId,
    "data-value": value,
    role: "option"
  }, props), children);
}

//
// Private function
// -------------------------------------------------------------------------------------------------

function extractLabel(child) {
  if ('label' in child.props) return child.props.label;
  if ('children' in child.props) return child.props.children;
  return child;
}